import { atom, getDefaultStore } from 'jotai'

import { api, FieldError, normalizeFieldErrors } from '../lib/api'
import { AUTH_TOKEN_KEY, SESSION_SNAPSHOT_KEY, getItem, setItem, removeItem } from '../lib/storage'
import { connectAtom as wsConnectAtom, disconnectAtom as wsDisconnectAtom } from './websocket'

type ErrorResponse = {
  error?: string
  daysRemaining?: number
  fields?: FieldError[]
}

export type Session = {
  user: {
    id: string
    email: string
    isActive: boolean
    isVerified: boolean
    subscriptionTier: string
    subscriptionStatus: string
    trialEndsAt?: string
    subscriptionEndsAt?: string
    remainingMatchCount: number
    resetRemainingMatchCountAt?: string
    profile?: {
      id: string
      userId: string
      name: string
      bio?: string
      birthday?: string
      gender?: string
      sexuality?: string
      location?: string
      latitude?: number
      longitude?: number
      maxDistance?: number
      mainPhotoId?: string
      photoIds: string[]
      approvedPhotoIds: string[]
      isActive: boolean
      isVisible: boolean
      createdAt: string
      updatedAt: string
    }
    createdAt: string
    updatedAt: string
  }
  token: {
    id: string
    value: string
    expiresAt: string
    createdAt: string
    lastUsedAt: string
    createdFrom: string
  }
  hasActiveAgentChat?: boolean
  /** Rheya left an outbound check-in; show badge until the member replies */
  rheyaHasMessage?: boolean
  /** Which agent path to open when badge/FAB fires: general | unmatch */
  rheyaPendingSource?: 'general' | 'unmatch' | ''
  /** Server-authoritative match id for incomplete unmatch interview */
  pendingUnmatchMatchId?: string
}

export const sessionAtom = atom<Session | null>(null)

export type SessionStatus = 'unknown' | 'authenticated' | 'unauthenticated'

export const sessionStatusAtom = atom<SessionStatus>('unknown')

export type OAuthProvider = 'google' | 'facebook'

export type FacebookLoginCredentials = {
  type: 'facebook'
  accessToken: string
  /** Required when linking to an existing password account */
  password?: string
}

export type GoogleLoginCredentials = {
  type: 'google'
  /** GIS credential JWT */
  idToken?: string
  /** GIS access token (openid email profile) */
  accessToken?: string
  /** Required when linking to an existing password account */
  password?: string
}

export type EmailAndPasswordLoginCredentials = {
  type: 'emailAndPassword'
  email: string
  password: string
}

export const loginIsLoadingAtom = atom(false)

export const loginIssuesAtom = atom<FieldError[]>([])

export type RejectionError = {
  code: 'rejected' | 'banned' | 'ipBlocked' | 'circumventionWarning'
  daysRemaining?: number | undefined
}

export const loginRejectionErrorAtom = atom<RejectionError | null>(null)

/** When OAuth finds a password account that is not yet linked. */
export type PasswordLinkRequired = {
  email: string
  provider: OAuthProvider
  idToken?: string
  accessToken?: string
  /** 'login' | 'register' — which flow to resume after password */
  flow: 'login' | 'register'
}

export const passwordLinkRequiredAtom = atom<PasswordLinkRequired | null>(null)

/** OAuth login when no member account exists yet — prompt to apply. */
export const oauthAccountNotFoundAtom = atom(false)

export type LoginCredentials =
  | FacebookLoginCredentials
  | GoogleLoginCredentials
  | EmailAndPasswordLoginCredentials

export type LoginResult =
  | { success: true }
  | { success: false; passwordLinkRequired?: true; accountNotFound?: true }

function persistCredentials(data: Session) {
  setItem(AUTH_TOKEN_KEY, data.token.value)
  setItem(SESSION_SNAPSHOT_KEY, JSON.stringify(data))
}

function clearCredentials() {
  removeItem(AUTH_TOKEN_KEY)
  removeItem(SESSION_SNAPSHOT_KEY)
}

function readSessionSnapshot(): Session | null {
  const raw = getItem(SESSION_SNAPSHOT_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Session
    if (!parsed?.user?.id || !parsed?.token?.value) return null
    return parsed
  } catch {
    return null
  }
}

async function applySession(set: (atom: any, value: any) => void, data: Session) {
  // Establish session first; never let WS setup block or reverse auth.
  api.setAuthToken(data.token.value)
  persistCredentials(data)
  set(sessionAtom, data)
  set(sessionStatusAtom, 'authenticated')
  try {
    void getDefaultStore().set(wsConnectAtom, data.token.value)
  } catch {
    // Multiplexed WS can reconnect later via ensureConnected.
  }
}

function isAuthRejection(status: number) {
  return status === 401 || status === 403
}

/** Establish a session from a server payload (login, OAuth, waitlist activate, etc.). */
export const establishSessionAtom = atom(
  null,
  async function establishSession(_get, set, data: Session) {
    await applySession(set, data)
  },
)

function parseErrorBody(data: any): ErrorResponse {
  if (!data || typeof data !== 'object') return {}
  const fields = normalizeFieldErrors(data)
  return {
    error: typeof data.error === 'string' ? data.error : undefined,
    daysRemaining: typeof data.daysRemaining === 'number' ? data.daysRemaining : undefined,
    fields: fields.length > 0 ? fields : undefined,
  }
}

export const loginAtom = atom(
  null,
  async function login(_get, set, credentials: LoginCredentials): Promise<LoginResult> {
    set(loginIsLoadingAtom, true)
    set(loginIssuesAtom, [])
    set(loginRejectionErrorAtom, null)
    set(oauthAccountNotFoundAtom, false)
    if (credentials.type === 'emailAndPassword') {
      set(passwordLinkRequiredAtom, null)
    }

    try {
      if (credentials.type === 'emailAndPassword') {
        const response = await api.fetch('/tokens', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: credentials.email,
            password: credentials.password,
          }),
        })

        if (!response.ok) {
          let data: ErrorResponse = {}
          try {
            data = parseErrorBody(await response.json())
          } catch {
            // non-JSON error body
          }
          set(loginIsLoadingAtom, false)

          if (response.status === 403 && data.error) {
            set(loginRejectionErrorAtom, {
              code: data.error as RejectionError['code'],
              daysRemaining: data.daysRemaining,
            })
          } else {
            set(loginIssuesAtom, data.fields?.length ? data.fields : [{ email: 'loginFailed' }])
          }

          set(sessionStatusAtom, 'unauthenticated')
          return { success: false }
        }

        const data = (await response.json()) as Session
        await applySession(set, data)
        set(loginIsLoadingAtom, false)
        return { success: true }
      }

      // Google / Facebook
      const provider: OAuthProvider = credentials.type
      const body: {
        provider: string
        idToken?: string
        accessToken?: string
        password?: string
      } = { provider }
      if (credentials.type === 'google') {
        if (credentials.idToken) body.idToken = credentials.idToken
        if (credentials.accessToken) body.accessToken = credentials.accessToken
      } else {
        body.accessToken = credentials.accessToken
      }
      if (credentials.password) body.password = credentials.password

      const response = await api.fetch('/tokens/oauth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        let data: any = {}
        try {
          data = await response.json()
        } catch {
          // non-JSON
        }
        set(loginIsLoadingAtom, false)

        if (response.status === 404 && data.error === 'accountNotFound') {
          set(oauthAccountNotFoundAtom, true)
          set(sessionStatusAtom, 'unauthenticated')
          return { success: false, accountNotFound: true }
        }

        if (response.status === 409 && data.error === 'passwordLinkRequired') {
          set(passwordLinkRequiredAtom, {
            email: data.email ?? '',
            provider,
            idToken: credentials.type === 'google' ? credentials.idToken : undefined,
            accessToken:
              credentials.type === 'google' ? credentials.accessToken : credentials.accessToken,
            flow: 'login',
          })
          set(sessionStatusAtom, 'unauthenticated')
          return { success: false, passwordLinkRequired: true }
        }

        if (response.status === 403 && data.error) {
          set(loginRejectionErrorAtom, {
            code: data.error as RejectionError['code'],
            daysRemaining: data.daysRemaining,
          })
        } else {
          set(loginIssuesAtom, data.fields?.length ? data.fields : [{ provider: 'loginFailed' }])
        }

        set(sessionStatusAtom, 'unauthenticated')
        return { success: false }
      }

      const data = (await response.json()) as Session
      set(passwordLinkRequiredAtom, null)
      await applySession(set, data)
      set(loginIsLoadingAtom, false)
      return { success: true }
    } catch {
      set(loginIsLoadingAtom, false)
      set(loginIssuesAtom, [{ email: 'networkError' }])
      set(sessionStatusAtom, 'unauthenticated')
      return { success: false }
    }
  },
)

export const clearPasswordLinkAtom = atom(null, function clearPasswordLink(_get, set) {
  set(passwordLinkRequiredAtom, null)
})

export const registerIsLoadingAtom = atom(false)

export const registerIssuesAtom = atom<FieldError[]>([])

export const registerSuccessAtom = atom(false)

export type RegisterCredentials = {
  email: string
  password: string
}

export type OAuthRegisterCredentials = {
  provider: OAuthProvider
  idToken?: string
  accessToken?: string
  password?: string
  osmId: number
  osmType: string
  latitude: number
  longitude: number
  location: string
  country: string
}

export type OAuthRegisterResult =
  | { success: true; kind: 'session' }
  | {
      success: true
      kind: 'waitlist'
      entry: { id: string; email: string; location: string; notifiedAt?: string; createdAt: string }
    }
  | { success: false; passwordLinkRequired?: true }

export const registerAtom = atom(
  null,
  async function register(_get, set, credentials: RegisterCredentials) {
    set(registerIsLoadingAtom, true)
    set(registerIssuesAtom, [])
    set(registerSuccessAtom, false)

    try {
      const response = await api.fetch('/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
        }),
      })

      if (!response.ok) {
        const data = (await response.json()) as ErrorResponse
        set(registerIssuesAtom, data.fields ?? [])
        set(registerIsLoadingAtom, false)
        return { success: false }
      }

      set(registerSuccessAtom, true)
      set(registerIsLoadingAtom, false)
      return { success: true }
    } catch (_err) {
      set(registerIssuesAtom, [{ email: 'networkError' }])
      set(registerIsLoadingAtom, false)
      return { success: false }
    }
  },
)

export const oauthRegisterAtom = atom(
  null,
  async function oauthRegister(
    _get,
    set,
    credentials: OAuthRegisterCredentials,
  ): Promise<OAuthRegisterResult> {
    set(registerIsLoadingAtom, true)
    set(registerIssuesAtom, [])
    set(registerSuccessAtom, false)
    set(loginRejectionErrorAtom, null)

    try {
      const body = {
        provider: credentials.provider,
        osmId: credentials.osmId,
        osmType: credentials.osmType,
        latitude: credentials.latitude,
        longitude: credentials.longitude,
        location: credentials.location,
        country: credentials.country,
        idToken: credentials.idToken,
        accessToken: credentials.accessToken,
        password: credentials.password,
      }

      const response = await api.fetch('/users/oauth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        let data: any = {}
        try {
          data = await response.json()
        } catch {
          // non-JSON
        }
        set(registerIsLoadingAtom, false)

        if (response.status === 409 && data.error === 'passwordLinkRequired') {
          set(passwordLinkRequiredAtom, {
            email: data.email ?? '',
            provider: credentials.provider,
            idToken: credentials.idToken,
            accessToken: credentials.accessToken,
            flow: 'register',
          })
          return { success: false, passwordLinkRequired: true }
        }

        if (response.status === 403 && data.error) {
          set(loginRejectionErrorAtom, {
            code: data.error as RejectionError['code'],
            daysRemaining: data.daysRemaining,
          })
        } else {
          set(registerIssuesAtom, data.fields ?? [{ provider: 'registerFailed' }])
        }
        return { success: false }
      }

      const data = (await response.json()) as {
        user?: Session['user']
        token?: Session['token']
        waitlist?: {
          id: string
          email: string
          location: string
          notifiedAt?: string
          createdAt: string
        }
        isActive: boolean
      }

      set(passwordLinkRequiredAtom, null)
      set(registerIsLoadingAtom, false)

      if (data.isActive && data.user && data.token) {
        await applySession(set, { user: data.user, token: data.token })
        return { success: true, kind: 'session' }
      }

      if (data.waitlist) {
        return { success: true, kind: 'waitlist', entry: data.waitlist }
      }

      set(registerIssuesAtom, [{ provider: 'registerFailed' }])
      return { success: false }
    } catch {
      set(registerIssuesAtom, [{ email: 'networkError' }])
      set(registerIsLoadingAtom, false)
      return { success: false }
    }
  },
)

export const resetRegisterAtom = atom(null, function resetRegister(_get, set) {
  set(registerSuccessAtom, false)
  set(registerIssuesAtom, [])
  set(passwordLinkRequiredAtom, null)
  set(oauthAccountNotFoundAtom, false)
})

export type VerifyEmailResult = {
  success: boolean
  session?: Session
}

export const verifyEmailIsLoadingAtom = atom(false)

export const verifyEmailIssuesAtom = atom<FieldError[]>([])

export const verifyEmailAtom = atom(
  null,
  async function verifyEmail(_get, set, token: string): Promise<VerifyEmailResult> {
    set(verifyEmailIsLoadingAtom, true)
    set(verifyEmailIssuesAtom, [])

    try {
      const response = await api.fetch('/users/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      })

      if (!response.ok) {
        const data = (await response.json()) as ErrorResponse
        set(verifyEmailIssuesAtom, data.fields ?? [])
        set(verifyEmailIsLoadingAtom, false)
        return { success: false }
      }

      const data = (await response.json()) as Session
      await applySession(set, data)
      set(verifyEmailIsLoadingAtom, false)
      return { success: true, session: data }
    } catch (_err) {
      set(verifyEmailIssuesAtom, [{ token: 'networkError' }])
      set(verifyEmailIsLoadingAtom, false)
      return { success: false }
    }
  },
)

export const logoutAtom = atom(null, async function logout(get, set) {
  getDefaultStore().set(wsDisconnectAtom)

  const currentSession = get(sessionAtom)
  if (currentSession) {
    try {
      await api.fetch(`/tokens/${currentSession.token.id}`, {
        method: 'DELETE',
      })
    } catch {
      // Still clear local credentials even if revoke fails offline
    }
  }

  api.clearAuthToken()
  clearCredentials()
  set(sessionAtom, null)
  set(sessionStatusAtom, 'unauthenticated')
})

export const updateSessionAtom = atom(null, async function updateSession(_get, set) {
  const response = await api.fetch('/tokens', {
    method: 'GET',
  })

  if (!response.ok) {
    // Only an auth rejection invalidates the local session. Transient errors
    // must not bounce the member to login while a valid token may still exist.
    if (isAuthRejection(response.status)) {
      api.clearAuthToken()
      clearCredentials()
      set(sessionAtom, null)
      set(sessionStatusAtom, 'unauthenticated')
    }
    return
  }

  const data = (await response.json()) as Session
  persistCredentials(data)
  set(sessionAtom, data)
  set(sessionStatusAtom, 'authenticated')
})

const RESTORE_FETCH_TIMEOUT_MS = 10_000

/**
 * Restore member session after cold start.
 *
 * 1. No token → unauthenticated
 * 2. Token + snapshot → hydrate immediately (correct app shell without flash)
 * 3. Revalidate with GET /tokens
 *    - 401/403 → clear credentials, unauthenticated
 *    - network / other errors → keep hydrated session if snapshot existed
 *    - ok → apply fresh session
 */
export const restoreAtom = atom(null, async function restoreSession(get, set) {
  const token = getItem(AUTH_TOKEN_KEY)
  if (!token) {
    // Do not clobber a login that completed while this restore was scheduled.
    if (get(sessionStatusAtom) === 'authenticated') return
    set(sessionAtom, null)
    set(sessionStatusAtom, 'unauthenticated')
    return
  }

  api.setAuthToken(token)

  const snapshot = readSessionSnapshot()
  if (snapshot) {
    const hydrated: Session = {
      ...snapshot,
      token: { ...snapshot.token, value: token },
    }
    set(sessionAtom, hydrated)
    set(sessionStatusAtom, 'authenticated')
    getDefaultStore().set(wsConnectAtom, token)
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), RESTORE_FETCH_TIMEOUT_MS)
  try {
    const response = await api.fetch('/tokens', {
      method: 'GET',
      signal: controller.signal,
    })

    // Token rotated under us (login during restore) — leave the new session alone.
    if (getItem(AUTH_TOKEN_KEY) !== token) return

    if (isAuthRejection(response.status)) {
      api.clearAuthToken()
      clearCredentials()
      set(sessionAtom, null)
      set(sessionStatusAtom, 'unauthenticated')
      return
    }

    if (!response.ok) {
      if (!snapshot && get(sessionStatusAtom) !== 'authenticated') {
        set(sessionAtom, null)
        set(sessionStatusAtom, 'unauthenticated')
      }
      return
    }

    const data = (await response.json()) as Session
    await applySession(set, data)
  } catch {
    if (getItem(AUTH_TOKEN_KEY) !== token) return
    if (snapshot || get(sessionStatusAtom) === 'authenticated') {
      set(sessionStatusAtom, 'authenticated')
      return
    }
    api.clearAuthToken()
    set(sessionAtom, null)
    set(sessionStatusAtom, 'unauthenticated')
  } finally {
    clearTimeout(timeoutId)
  }
})

/** In-memory only — does not clear durable credentials (use logout for that). */
export function markUnauthenticated() {
  api.clearAuthToken()
  const store = getDefaultStore()
  store.set(sessionAtom, null)
  store.set(sessionStatusAtom, 'unauthenticated')
}

export async function restore() {
  await getDefaultStore().set(restoreAtom)
}

// Password Reset State Management
export const forgotPasswordIsLoadingAtom = atom(false)

export const forgotPasswordIssuesAtom = atom<FieldError[]>([])

export const forgotPasswordSuccessAtom = atom(false)

export const forgotPasswordRequestAtom = atom(
  null,
  async function requestPasswordReset(_get, set, email: string) {
    set(forgotPasswordIsLoadingAtom, true)
    set(forgotPasswordIssuesAtom, [])
    set(forgotPasswordSuccessAtom, false)

    try {
      const response = await api.fetch('/password-resets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      if (response.ok) {
        set(forgotPasswordSuccessAtom, true)
        set(forgotPasswordIsLoadingAtom, false)
        return { success: true }
      }

      if (response.status === 429) {
        set(forgotPasswordIssuesAtom, [{ email: 'rateLimitExceeded' }])
      } else {
        const data = (await response.json()) as ErrorResponse
        set(forgotPasswordIssuesAtom, data.fields ?? [{ email: 'networkError' }])
      }
      return { success: false }
    } catch (_err) {
      set(forgotPasswordIssuesAtom, [{ email: 'networkError' }])
      return { success: false }
    } finally {
      set(forgotPasswordIsLoadingAtom, false)
    }
  },
)

export const resetPasswordIsLoadingAtom = atom(false)

export const resetPasswordIssuesAtom = atom<FieldError[]>([])

export const resetPasswordSuccessAtom = atom(false)

export const resetPasswordConfirmAtom = atom(
  null,
  async function confirmPasswordReset(_get, set, token: string, newPassword: string) {
    set(resetPasswordIsLoadingAtom, true)
    set(resetPasswordIssuesAtom, [])
    set(resetPasswordSuccessAtom, false)

    try {
      const response = await api.fetch('/password-resets/confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      })

      if (response.ok) {
        set(resetPasswordSuccessAtom, true)
        api.clearAuthToken()
        clearCredentials()
        set(sessionAtom, null)
        set(sessionStatusAtom, 'unauthenticated')
        set(resetPasswordIsLoadingAtom, false)
        return { success: true }
      }

      const data = (await response.json()) as ErrorResponse
      set(resetPasswordIssuesAtom, data.fields ?? [{ token: 'networkError' }])
      return { success: false }
    } catch (_err) {
      set(resetPasswordIssuesAtom, [{ token: 'networkError' }])
      return { success: false }
    } finally {
      set(resetPasswordIsLoadingAtom, false)
    }
  },
)

export const resetForgotPasswordAtom = atom(null, function resetForgotPassword(_get, set) {
  set(forgotPasswordSuccessAtom, false)
  set(forgotPasswordIssuesAtom, [])
})

export const resetResetPasswordAtom = atom(null, function resetResetPassword(_get, set) {
  set(resetPasswordSuccessAtom, false)
  set(resetPasswordIssuesAtom, [])
})
